import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { Vault, ShieldCheck, Lock, Fingerprint } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", company: "" });
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
      } else {
        await register({
          email: form.email,
          password: form.password,
          name: form.name,
          company: form.company,
        });
      }
      toast.success("Vault unlocked");
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-background">
      {/* Left brand panel */}
      <div className="relative hidden w-1/2 flex-col justify-between overflow-hidden bg-card p-12 lg:flex">
        <div className="vault-grid-bg absolute inset-0 opacity-60" />
        <div className="relative flex items-center gap-2.5">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <Vault className="h-6 w-6" />
          </div>
          <span className="text-xl font-bold tracking-tight">CITIDAN</span>
        </div>
        <div className="relative space-y-6">
          <h1 className="text-4xl font-bold leading-tight tracking-tight lg:text-5xl">
            The vault where
            <br />
            <span className="text-primary">secrets stay secret.</span>
          </h1>
          <p className="max-w-md text-base leading-relaxed text-muted-foreground">
            Store payment-processor credentials, tokenize raw card data, and safeguard crypto keys.
            Everything encrypted server-side, shown only masked — raw secrets never return to the screen.
          </p>
          <div className="flex flex-col gap-3 pt-2">
            {[
              [Lock, "AES-256 server-side encryption"],
              [Fingerprint, "tok_ card tokenization engine"],
              [ShieldCheck, "Role-based merchant isolation"],
            ].map(([Icon, text], i) => (
              <div key={i} className="flex items-center gap-3 text-sm text-muted-foreground">
                <Icon className="h-4 w-4 text-primary" />
                {text}
              </div>
            ))}
          </div>
        </div>
        <div className="relative text-xs font-mono uppercase tracking-wider text-muted-foreground">
          CITIDAN Secure Vault · v1.0
        </div>
      </div>

      {/* Right form */}
      <div className="flex w-full items-center justify-center p-6 lg:w-1/2">
        <div className="w-full max-w-sm">
          <div className="mb-8 lg:hidden">
            <div className="flex items-center gap-2.5">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                <Vault className="h-5 w-5" />
              </div>
              <span className="text-lg font-bold">CITIDAN</span>
            </div>
          </div>

          <h2 className="text-2xl font-semibold tracking-tight">
            {mode === "login" ? "Sign in to your vault" : "Create merchant account"}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {mode === "login"
              ? "Enter your credentials to access the vault."
              : "Register as a merchant to start securing data."}
          </p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            {mode === "register" && (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="name">Full name</Label>
                  <Input id="name" data-testid="name-input" value={form.name} onChange={set("name")} required placeholder="Jane Merchant" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="company">Company</Label>
                  <Input id="company" data-testid="company-input" value={form.company} onChange={set("company")} placeholder="Acme Inc." />
                </div>
              </>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" data-testid="email-input" value={form.email} onChange={set("email")} required placeholder="you@company.com" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" data-testid="password-input" value={form.password} onChange={set("password")} required placeholder="••••••••" />
            </div>

            {error && (
              <div data-testid="auth-error" className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </div>
            )}

            <Button type="submit" disabled={loading} data-testid="submit-btn" className="w-full">
              {loading ? "Please wait…" : mode === "login" ? "Unlock Vault" : "Create Account"}
            </Button>
          </form>

          <button
            onClick={() => {
              setMode(mode === "login" ? "register" : "login");
              setError("");
            }}
            data-testid="toggle-mode-btn"
            className="mt-5 w-full text-center text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            {mode === "login" ? "New merchant? Create an account" : "Already registered? Sign in"}
          </button>
        </div>
      </div>
    </div>
  );
}
